import { EventEmitter, Injectable } from '@angular/core';
import {
	BehaviorSubject,
	catchError,
	delay,
	filter,
	forkJoin,
	from,
	map,
	mergeMap,
	Observable,
	of,
	Subscription,
	switchMap,
	take,
	takeWhile,
	tap,
	throwError,
	timer,
	toArray,
} from 'rxjs';
import {
	ErrorResponse,
	LoginInfo,
	LoginResponse,
	OrgReturn,
	Roles,
	Tag as TagDefinition,
	userReturn,
} from '@meta-press/shared-plugins';
import { AiService } from '../services/ai/ai.service';
import { VectorStore } from 'openai/resources/index.mjs';
import { environment } from '../../../environments/environment';
import { Router } from '@angular/router';
import { NotificationService } from '../notification-service/notification.service';
import { BackendService } from '../backend-service/backend.service';
import { AuthTokenService } from '../services/auth-token.service';

@Injectable({
	providedIn: 'root',
})
export class LoginService {
	private users: LoginInfo[] = [];
	private organizations: OrgReturn[] = [];
	private currentUser: LoginInfo | undefined = undefined;
	private activeSubscriptions: Subscription[] = [];
	private usersLoaded = false;
	private organizationsLoaded = false;

	public get CurrentUser(): LoginInfo | undefined {
		return this.users.find((user) => user.id === this.currentUser?.id);
	}

	private isLoggingIn = false;
	public get IsLoggingIn(): boolean {
		return this.isLoggingIn;
	}

	private isLoadingUsers = false;
	public get IsLoadingUsers(): boolean {
		return this.isLoadingUsers;
	}

	private isLoadingOrganizations = false;
	public get IsLoadingOrganizations(): boolean {
		return this.isLoadingOrganizations;
	}

	public get Users(): LoginInfo[] {
		return this.users;
	}

	public get Organizations(): OrgReturn[] {
		return this.organizations;
	}

	constructor(
		private backendService: BackendService,
		private aiService: AiService,
		private router: Router, // Import Router for navigation
		private notificationService: NotificationService,
		private authTokenService: AuthTokenService,
	) {
		// Get list of users
	}

	public RefreshUsers(): Observable<userReturn[]> {
		// Reset loaded flag to force fresh data on next access
		this.usersLoaded = false;

		if (this.currentUser?.permission === Roles.SuperAdmin) {
			return this.backendService.GetUsers().pipe(
				switchMap((users: userReturn[]) => {
					if (users.length === 0) {
						// If no users, return an empty array
						return of([]);
					} else {
						// Otherwise, map the users to LoginInfo
						return from(users).pipe(
							mergeMap((user: userReturn) =>
								this.backendService.GetUser(user.id),
							),
							toArray(),
						);
					}
				}),
				catchError((error: any) => {
					const errorMessage =
						error?.errors?.join(', ') ||
						error?.message ||
						'Unknown error';
					return throwError(
						() =>
							new Error('Error fetching users: ' + errorMessage),
					);
				}),
				map((users: userReturn[]) => {
					this.users = users.map((user: userReturn) => {
						const loginInfo: LoginInfo = {
							userName: user.userName || '',
							organizationId: user.org || -1,
							firstName: user.firstName || '',
							lastName: user.lastname || '',
							email: user.email || '',
							permission: user.role,
							id: user.id || '',
							password: '',
							vectorStoreId: '',
							// vectorStoreName: '',
							openAIApiKey: user.key || '',
						} as LoginInfo;
						return loginInfo;
					});
					this.users.sort((a, b) =>
						a.userName.localeCompare(b.userName),
					);

					this.isLoggingIn = false;
					this.userListSubject.next(this.users);

					return users;
				}),
			);
		} else if (this.currentUser?.permission === Roles.Admin) {
			// For Admins, we can fetch users from the same organization
			return this.backendService
				.GetORg(this.currentUser.organizationId)
				.pipe(
					switchMap((organization: OrgReturn) => {
						if (organization.users.length === 0) {
							// If no users, return an empty array
							return of([]);
						} else {
							// Otherwise, map the users to LoginInfo
							return from(organization.users).pipe(
								mergeMap((user: userReturn) =>
									this.backendService.GetUser(user.id),
								),
								toArray(),
							);
						}
					}),
					catchError((error: any) => {
						const errorMessage =
							error?.errors?.join(', ') ||
							error?.message ||
							'Unknown error';
						return throwError(
							() =>
								new Error(
									'Error fetching users: ' + errorMessage,
								),
						);
					}),
					map((users: userReturn[]) => {
						this.users = users.map((user: userReturn) => {
							const loginInfo: LoginInfo = {
								userName: user.userName || '',
								organizationId: user.org || -1,
								firstName: user.firstName || '',
								lastName: user.lastname || '',
								email: user.email || '',
								permission: user.role,
								id: user.id || '',
								password: '',
								vectorStoreId: '',
								// vectorStoreName: '',
								openAIApiKey: user.key || '',
							} as LoginInfo;
							return loginInfo;
						});
						this.users.sort((a, b) =>
							a.userName.localeCompare(b.userName),
						);

						this.isLoggingIn = false;
						this.userListSubject.next(this.users);

						return users;
					}),
				);
		} else {
			// For regular users, return an empty array
			return of([]);
		}
	}

	public RefreshOrganizations(): Observable<OrgReturn[]> {
		// Reset loaded flag to force fresh data on next access
		this.organizationsLoaded = false;

		return this.backendService.GetORgs().pipe(
			switchMap((orgs: OrgReturn[]) => {
				// GetORgs returns a single OrgReturn, but we expect an array pattern
				// If no organizations, return an empty array
				if (!orgs) {
					return of([]);
				} else {
					// For now, return the single org as an array
					// If backend later supports multiple orgs, this can be updated
					return of(orgs);
				}
			}),
			map((organizations: OrgReturn[]) => {
				this.organizations = organizations;
				this.organizations.sort((a, b) => a.name.localeCompare(b.name));

				this.organizationListSubject.next(this.organizations);

				return organizations;
			}),
		);
	}

	// public UseHasPermissionFor(permissions: UserPermissionType[]): boolean {
	// 	if (!this.currentUser) {
	// 		return false;
	// 	}

	// 	const userPermissions = this.currentUser.permission;

	// 	return permissions.some((permission) =>
	// 		this.HasPermission(userPermissions, permission),
	// 	);
	// }

	public OnLogin: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
		false,
	);

	public OnLogout: EventEmitter<boolean> = new EventEmitter<boolean>();

	private userListSubject: BehaviorSubject<LoginInfo[]> = new BehaviorSubject<
		LoginInfo[]
	>([]);

	private organizationListSubject: BehaviorSubject<OrgReturn[]> =
		new BehaviorSubject<OrgReturn[]>([]);

	public get OnUserList(): Observable<LoginInfo[]> {
		return this.userListSubject.asObservable();
	}

	public get OnOrganizationList(): Observable<OrgReturn[]> {
		return this.organizationListSubject.asObservable();
	}

	public GetCurrentUser(): LoginInfo | undefined {
		return this.currentUser;
	}

	public GetFullName(user: LoginInfo): string {
		return user.firstName + user.lastName !== '' ? ' ' + user.lastName : '';
	}

	/**
	 * Adds a subscription to be automatically cancelled on logout
	 * @param subscription The subscription to manage
	 */
	private addActiveSubscription(subscription: Subscription): void {
		this.activeSubscriptions.push(subscription);
	}

	/**
	 * Cancels all active subscriptions and clears the list
	 */
	private cancelAllActiveSubscriptions(): void {
		this.activeSubscriptions.forEach((subscription) => {
			if (subscription && !subscription.closed) {
				subscription.unsubscribe();
			}
		});
		this.activeSubscriptions = [];
		console.log('All active subscriptions cancelled');
	}

	/**
	 * Lightweight re-authentication method that only gets new tokens
	 * without fetching user info or updating UI state.
	 * Used for re-login modals when session expires.
	 */
	public ReAuthenticate(
		userName: string,
		password: string,
	): Observable<boolean> {
		// Set login in progress state for re-authentication
		this.authTokenService.setLoginInProgress(true);

		return (
			this.backendService.Login(
				userName,
				password,
				false,
				false,
			) as Observable<LoginResponse | undefined>
		).pipe(
			catchError((error: any) => {
				// Reset login in progress state on re-authentication error
				this.authTokenService.setLoginInProgress(false);
				const errorMessage =
					error?.errors?.join(', ') ||
					error?.message ||
					'Unknown error';
				console.error('Re-authentication failed:', errorMessage);
				return throwError(
					() =>
						new Error('Re-authentication failed: ' + errorMessage),
				);
			}),
			tap((loginResponse: LoginResponse | undefined) => {
				// Only set tokens in AuthTokenService after successful re-authentication
				if (
					loginResponse?.accessToken &&
					loginResponse?.refreshToken &&
					loginResponse?.expiresIn
				) {
					this.authTokenService.setTokens(
						loginResponse.accessToken,
						loginResponse.refreshToken,
						loginResponse.expiresIn,
					);
					// Reset login in progress state on successful re-authentication
					this.authTokenService.setLoginInProgress(false);
					console.log('Re-authentication successful, tokens updated');
				} else {
					// Reset login in progress state even on invalid response
					this.authTokenService.setLoginInProgress(false);
					throw new Error('Invalid login response - missing tokens');
				}
			}),
			map(() => true), // Return success boolean
		);
	}

	public Login(userName: string, password: string): Observable<boolean> {
		this.isLoggingIn = true;
		// Set login in progress state in AuthTokenService
		this.authTokenService.setLoginInProgress(true);

		return (
			this.backendService.Login(
				userName,
				password,
				false,
				false,
			) as Observable<LoginResponse | undefined>
		).pipe(
			catchError((error: any) => {
				this.isLoggingIn = false;
				// Reset login in progress state on error
				this.authTokenService.setLoginInProgress(false);
				const errorMessage =
					error?.errors?.join(', ') ||
					error?.message ||
					'Unknown error';
				console.error('Login failed:', errorMessage);
				return throwError(
					() => new Error('Login failed: ' + errorMessage),
				);
			}),
			tap((loginResponse: LoginResponse | undefined) => {
				// Set tokens in AuthTokenService after successful login
				if (
					loginResponse?.accessToken &&
					loginResponse?.refreshToken &&
					loginResponse?.expiresIn
				) {
					this.authTokenService.setTokens(
						loginResponse.accessToken,
						loginResponse.refreshToken,
						loginResponse.expiresIn,
					);
				}
			}),
			switchMap(() => {
				// Get user info after successful login
				console.log('Login successful, fetching user info...');

				return this.backendService.GetCurrentUserInfo().pipe(
					catchError((error: any) => {
						this.isLoggingIn = false;
						const errorMessage =
							error?.errors?.join(', ') ||
							error?.message ||
							'Unknown error';
						console.error(
							'Error fetching current user info:',
							errorMessage,
						);
						return throwError(
							() =>
								new Error(
									'Error fetching current user info: ' +
										errorMessage,
								),
						);
					}),
					map((user: userReturn) => {
						// Set current user name
						return {
							userName: user.userName || '',
							organizationId: user.org || -1,
							firstName: user.firstName || '',
							lastName: user.lastname || '',
							email: user.email || '',
							permission: user.role,
							id: user.id || '',
							password: '', // Password should not be stored
							vectorStoreId: '',
							// vectorStoreName: '',
							openAIApiKey: user.key || '', // || '',
						} as LoginInfo;

						// console.log('Current user set to:', this.currentUser);

						// return this.currentUser;
						// Update OpenAI service with the user's API key
					}),
				);
			}),
			// Initialize AI api
			tap((user: LoginInfo) => {
				// Initialize AI service with the user's API key - this doesn't matter because api key comes from server
				this.aiService.InitializeClientAPI(user.openAIApiKey);

				// this.aiService.InitializeAdminClientAPI(
				// 	environment.openAIProjectID,
				// );

				console.log(
					'Initialized api key. Refreshing user list after login',
				);
			}),

			// Process the results
			map((user: LoginInfo) => {
				this.currentUser = user;

				console.log('Vector store setup complete, login successful');

				return true;
			}),
			catchError((error: any) => {
				this.isLoggingIn = false;
				const errorMessage =
					error?.errors?.join(', ') ||
					error?.message ||
					error?.toString() ||
					'Unknown error';
				console.error(
					'Critical error during login setup:',
					errorMessage,
				);
				return throwError(
					() =>
						new Error(
							'Critical error during login setup: ' +
								errorMessage,
						),
				);
			}),
			tap(() => {
				this.isLoggingIn = false;
				// Reset login in progress state on successful completion
				this.authTokenService.setLoginInProgress(false);
				this.OnLogin.next(true);

				console.log('Login complete.');
				// Initiating deleting of redundant vector store files (cancelled or failed)

				if (this.currentUser) {
					const cleanupSubscription = this.aiService
						.ClearVectorStore()
						.subscribe({
							next: () => {
								console.log(
									'Vector store files cleanup completed.',
								);
							},
							error: (error) => {
								console.error(
									'Error during vector store files cleanup:',
									error,
								);
							},
						});

					// Add to active subscriptions for automatic cleanup on logout
					this.addActiveSubscription(cleanupSubscription);
				}
			}),
		) as Observable<boolean>;
	}

	/**
	 * Ensures users are loaded (lazy loading)
	 * @returns Observable that completes when users are loaded
	 */
	public ensureUsersLoaded(): Observable<boolean> {
		if (this.usersLoaded) {
			console.log('Users already loaded, using cached data');
			return of(true);
		}

		if (this.isLoadingUsers) {
			console.log(
				'Users are already being loaded, waiting for completion...',
			);
			// Poll every 100ms until loading is complete, then return final result
			return timer(0, 100).pipe(
				takeWhile(() => this.isLoadingUsers, true),
				// Only emit the final result after loading completes
				map(() => this.usersLoaded),
				// Filter out intermediate false values, only let true through
				// or emit false at the end if loading fails
				filter((loaded) => loaded || !this.isLoadingUsers),
				tap((loaded) => {
					if (loaded) {
						console.log('Users loading completed while waiting');
					}
				}),
				// Take only the first valid result
				take(1),
			);
		}

		if (!this.currentUser) {
			console.log('No current user, cannot load users');
			return of(false);
		}

		if (
			this.currentUser.permission !== Roles.Admin &&
			this.currentUser.permission !== Roles.SuperAdmin
		) {
			console.log('User does not have permission to view users');
			this.usersLoaded = true; // Mark as loaded since there's nothing to load
			return of(true);
		}

		console.log('Loading users on demand...');
		this.isLoadingUsers = true;
		return this.RefreshUsers().pipe(
			map((users) => {
				this.usersLoaded = true;
				this.isLoadingUsers = false;
				console.log('Users loaded successfully:', users.length);
				return true;
			}),
			catchError((error) => {
				console.error('Failed to load users:', error);
				this.isLoadingUsers = false;
				return of(false);
			}),
		);
	}

	/**
	 * Ensures organizations are loaded (lazy loading)
	 * @returns Observable that completes when organizations are loaded
	 */
	public ensureOrganizationsLoaded(): Observable<boolean> {
		if (this.organizationsLoaded) {
			console.log('Organizations already loaded, using cached data');
			return of(true);
		}

		if (this.isLoadingOrganizations) {
			console.log(
				'Organizations are already being loaded, waiting for completion...',
			);
			// Poll every 100ms until loading is complete, then return final result
			return timer(0, 100).pipe(
				takeWhile(() => this.isLoadingOrganizations, true),
				// Only emit the final result after loading completes
				map(() => this.organizationsLoaded),
				// Filter out intermediate false values, only let true through
				// or emit false at the end if loading fails
				filter((loaded) => loaded || !this.isLoadingOrganizations),
				tap((loaded) => {
					if (loaded) {
						console.log(
							'Organizations loading completed while waiting',
						);
					}
				}),
				// Take only the first valid result
				take(1),
			);
		}

		if (!this.currentUser) {
			console.log('No current user, cannot load organizations');
			return of(false);
		}

		if (this.currentUser.permission !== Roles.SuperAdmin) {
			console.log('User does not have permission to view organizations');
			this.organizationsLoaded = true; // Mark as loaded since there's nothing to load
			return of(true);
		}

		console.log('Loading organizations on demand...');
		this.isLoadingOrganizations = true;
		return this.RefreshOrganizations().pipe(
			map((orgs) => {
				this.organizationsLoaded = true;
				this.isLoadingOrganizations = false;
				console.log('Organizations loaded successfully:', orgs.length);
				return true;
			}),
			catchError((error) => {
				console.error('Failed to load organizations:', error);
				this.isLoadingOrganizations = false;
				return of(false);
			}),
		);
	}

	public Logout(): void {
		// Cancel all active subscriptions first
		this.cancelAllActiveSubscriptions();

		this.isLoggingIn = false;
		this.currentUser = undefined;
		this.users = [];
		this.organizations = [];
		this.usersLoaded = false;
		this.organizationsLoaded = false;
		this.userListSubject.next([]);
		this.organizationListSubject.next([]);
		this.OnLogin.next(false);
		console.log('User logged out and state reset.');

		// Clear tokens from AuthTokenService
		this.authTokenService.clearTokens();

		// De-initialize OpenAI client
		this.aiService.DeInitializeClientAPI();

		this.OnLogout.next(true);

		// Navigate to login page
		this.router.navigate(['/login']);
	}
}
